<template>
  <div class="relative full-height full-width">
    <canvas ref="canvas" class="chart-canvas"></canvas>
    <slot name="chart-body"></slot>
    <Transition>
      <div
        v-if="showTooltip && currentPoint"
        ref="tooltip"
        :style="tooltipStyle"
        :class="['custom-tooltip']"
      >
        <slot name="tooltip">
          <div class="d-flex">
            <span class="tooltip-label">{{ currentPoint.label }}</span>
            <span>{{ currentPoint.value }}</span>
          </div>
        </slot>
      </div>
    </Transition>
  </div>
</template>

<script>
import Chart from "chart.js";
import { nextTick } from "vue";
import {
  colorPalette,
  doughnutTextPlugin,
  gridLineBackgroundPlugin,
  typeDefaults
} from "@/lib/charts.js";

import Transition from "@/components/Transition.vue";
import "chartjs-plugin-annotation";
import "chartjs-plugin-style";

export default {
  components: {
    Transition
  },
  props: {
    formatYAxis: {
      type: Function,
      default: (value) => value
    },
    innerText: {
      type: String,
      default: ""
    },
    labels: {
      type: Array,
      default: () => []
    },
    options: {
      type: Object,
      default: () => ({})
    },
    series: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ""
    },
    type: {
      type: String,
      default: "line"
    },
    size: {
      type: String,
      default: ""
    },
    onPointClick: {
      type: Function
    }
  },
  emits: ["point-click", "point-hover"],
  data() {
    return {
      chart: null,
      showTooltip: "",
      tooltipStyle: "",
      currentPoint: null,
      mousePosition: {}
    };
  },
  computed: {
    isClickable() {
      return this.$props.onPointClick;
    }
  },
  watch: {
    series(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateChartData();
      }
    }
  },
  mounted() {
    this.initChart();
  },
  methods: {
    initChart() {
      Chart.defaults.global.defaultFontFamily = "Inter";
      const data = this.getData();
      const options = this.getOptions();
      const plugins = this.getPlugins();
      this.chart = new Chart(this.$refs.canvas, {
        type: this.type,
        plugins,
        data,
        options
      });
    },
    getColors(index) {
      return {
        borderColor: colorPalette[index],
        pointBackgroundColor: colorPalette[index],
        pointHoverBackgroundColor: "#FFF",
        pointHoverBorderColor: colorPalette[index]
      };
    },
    getData() {
      const datasets = this.series.map((dataset, i) => {
        return {
          ...typeDefaults.datasets[this.type],
          ...this.getColors(i),
          ...dataset
        };
      });
      return {
        labels: this.labels,
        datasets
      };
    },
    getLegend() {
      const customLegend = this.series.some((dataset) => dataset.legendLabel);
      let legend = {
        position: "top",
        align: "end",
        ...this.options.legend
      };
      if (customLegend) {
        legend = {
          ...legend,
          labels: {
            fontColor: "#222",
            generateLabels(chart) {
              const { data } = chart;
              if (data.datasets.length) {
                return data.datasets.map((dataset, i) => {
                  const meta = chart.getDatasetMeta(i);
                  const style = meta.controller.getStyle(i);
                  return {
                    text: dataset.legendLabel || dataset.label,
                    fillStyle: style.backgroundColor,
                    strokeStyle: dataset.borderColor,
                    lineWidth: dataset.borderWidth,
                    hidden: dataset.hidden,
                    lineDash: dataset.borderDash,
                    // Extra data used for toggling the correct item
                    index: i
                  };
                });
              }
              return [];
            }
          },
          onClick: function (e, legendItem) {
            const dataset = this.chart.data.datasets[legendItem.index];
            dataset.hidden = !dataset.hidden;
            this.chart.update();
          }
        };
      }
      return legend;
    },
    getOptions() {
      const self = this;
      const legend = this.getLegend();
      const options = {
        maintainAspectRatio: false,
        tooltips: {
          ...this.getTooltip()
        },
        onHover: (event, chartElement) => {
          // Set line points to cursor when clickable
          if (this.isClickable) {
            event.target.style.cursor = chartElement[0] ? "pointer" : "default";
          }
          self.mousePosition = {
            x: Number(event.clientX) || event.layerX,
            y: Number(event.clientY) || event.layerY
          };
        },
        onClick: function (e, items) {
          const item = (this.chart.getElementAtEvent(e) || [])[0];
          if (item) {
            self.$emit("point-click", {
              dataset: this.data.datasets[item._datasetIndex],
              pointIndex: item._index,
              items,
              e
            });
          }
        },
        scales: {
          yAxes: [
            {
              gridLines: {
                backgroundColor: "#FFF"
              },
              ticks: {
                callback: this.formatYAxis
              }
            }
          ],
          xAxes: [
            {
              gridLines: { display: false, backgroundColor: "#FFF" },
              ticks: {
                maxRotation: 0,
                autoSkipPadding: 5
              }
            }
          ]
        },
        legend,
        ...typeDefaults.options[this.type],
        ...this.options
      };

      if (this.innerText) {
        options.elements = {
          ...options.elements,
          center: {
            text: this.innerText
          }
        };
      }

      if (this.title) {
        options.title = {
          display: true,
          text: this.title
        };
      }

      return options;
    },
    getPlugins() {
      const plugins = [];
      if (this.type === "doughnut" && this.innerText) {
        plugins.push(doughnutTextPlugin);
      } else {
        plugins.push(gridLineBackgroundPlugin);
      }
      return plugins;
    },
    getTooltip() {
      const self = this;
      return {
        // Disable the on-canvas tooltip
        enabled: false,
        custom: async function (tooltipModel) {
          if (tooltipModel.dataPoints) {
            self.$emit("point-hover", {
              point: tooltipModel.dataPoints[0],
              context: tooltipModel
            });
            self.currentPoint = tooltipModel.dataPoints[0];
          }
          if (tooltipModel.opacity === 0) {
            self.showTooltip = false;
            this.tooltipStyle = "top: -100px; left: -100px";
          } else {
            self.showTooltip = true;
            await nextTick();
            self.getTooltipPosition();
          }
        }
      };
    },
    getTooltipPosition() {
      const yOffset = this.$refs.tooltip.offsetHeight + 2;
      const tooltipX = this.mousePosition.x;
      const tooltipY = this.mousePosition.y - yOffset;
      this.tooltipStyle = `top: ${tooltipY}px; left: ${tooltipX}px; position: fixed; z-index: 2;`;
    },
    updateChartData() {
      this.chart.data = this.getData();
      this.chart.options = this.getOptions();
      this.chart.update();
    }
  }
};
</script>

<style lang="scss">
$tooltip-background-color: rgba(0, 0, 0, 0.9);
.chart-canvas {
  background-color: transparent;
}
.custom-tooltip {
  font-size: 13px;
  position: absolute;
  color: white;
  background-color: $tooltip-background-color;
  padding: 6px 10px;
  border-radius: 6px;
  pointer-events: none;
  .tooltip-label {
    opacity: 0.8;
    margin-right: 10px;
  }
}
</style>
